import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Button, Chip, Divider, Tooltip, Typography, Stack, } from "@mui/material";
import { DocumentContentActions } from "../documentsApi"
import { unwrapResult } from "@reduxjs/toolkit";
import { AppId } from "../../../app/appTypes";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { Edit, Delete, Add } from "@mui/icons-material";
import { Localized } from "@fluent/react";
import produce from "immer";
import { If } from "../../../app/If";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CommentIcon from '@mui/icons-material/Comment'
import { isDocEditable, selectDocument, showDocComments } from "../documentSlice";
import { AllCommentsCounter } from "../CommentsCounter";
import { DocumentDqMapping, loadDocumentDqMappings, updateDqMappingForReport } from "./documentDqMappingsApi";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { GenerateDqMappingsDialog } from "./dialogs/GenerateDqMappingsDialog";
import { DocumentNumberBox } from "../../../app/components/DocumentNumberBox";
import { AddEditDocumentDqMapping } from "./dialogs/AddEditDocumentDqMapping";
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import { DeleteDqMappingDialog } from "./dialogs/DeleteDqMappingDialog";
import { PrintPreviewDocumentDqMappingsView } from "./PrintPreviewDocumentDqMappings";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { impossible } from "../../../app/Utils";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import PrintIcon from "@mui/icons-material/Print";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { DocumentOriginIcon, LockedIconWithTooltip } from "./components/DqMappingOriginUtils";
import { BulkAssessDocumentDqMappingsDialog } from "./dialogs/BulkAssessDocumentDqMappingsDialog";

type DialogState
  = "none"
  | "addEditDocumentDqMapping"
  | "deleteDocumentDqMapping"
  | "commentDocumentDqMapping"
  | "generateDocumentDqMappings"
  | "bulkAssessDocumentDqMappings"

export interface DocumentQuestionViewProps {
  documentId: AppId
  sectionId: AppId
  actions: DocumentContentActions[],
  enablePreview?: boolean
  reloadToken?: number
  aiGenerationAction?: ReactNode
}

function hasAction(
  actions: DocumentContentActions[],
  action: DocumentContentActions
): boolean {
  return actions.includes(action);
}

export interface AnalyzedObject {
  id: number;
  name: string;
  answers: Record<number, string | null>;
}

type DqMappingNote = "OK" | "FAIL" | "NA";
const NUM_COL_WIDTH = 72;    // Nr (węższa)
const DOC_COL_PCT = "24%";   // Dokument (FS)
const REQ_COL_PCT = "35%";   // Wymaganie (URS)
const DESC_COL_PCT = "22%";  // Opis / Komentarz
const NOTE_COL_WIDTH = 180;  // Ocena + komentarz mapowania
const ACTIONS_COL_WIDTH = 200;
const PROTOCOL_ACTIONS_COL_WIDTH = 100;
const PROTOCOL_ACTIONS_WITH_COMMENTS_COL_WIDTH = 150;
const COLLAPSED_ACTIONS_COL_WIDTH = 96;

const NOTE_META: Record<DqMappingNote, {
  labelId: string;
  fallback: string;
  color: "success" | "error" | "default";
  Icon: typeof CheckCircleOutlineIcon;
}> = {
  OK: { labelId: "document-dq-mapping-note-ok", fallback: "Fulfills", color: "success", Icon: CheckCircleOutlineIcon },
  FAIL: { labelId: "document-dq-mapping-note-fail", fallback: "Does not fulfill", color: "error", Icon: CancelOutlinedIcon },
  NA: { labelId: "document-dq-mapping-note-na", fallback: "Not applicable", color: "default", Icon: RemoveCircleOutlineIcon },
};

export const NoteChip: React.FC<{ note: DqMappingNote | null | undefined }> = ({ note }) => {
  const meta = note ? NOTE_META[note] : null;

  if (!meta) return null;

  const { Icon, labelId, fallback, color } = meta;
  return (
    <Chip
      size="small"
      icon={<Icon />}
      label={<Localized id={labelId}><span>{fallback}</span></Localized>}
      color={color}
      variant={color === "default" ? "outlined" : "filled"}
      sx={{ ".MuiChip-icon": { mr: 0.5 } }}
    />
  );
};

// skracanie HTML do 3 linii
const ClampHtml: React.FC<{ html: string; lines?: number }> = ({ html, lines = 3 }) => (
  <Box sx={{
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    "& *": { margin: 0 }
  }}>
    <div dangerouslySetInnerHTML={{ __html: html }} />
  </Box>
);

export const DocumentDqMappingsView = ({ documentId, sectionId, actions, enablePreview = false, reloadToken = 0, aiGenerationAction }: DocumentQuestionViewProps) => {
  const dispatch = useAppDispatch()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [error, setError] = useState(null);
  const [selectedMapping, setSelectedMapping] = useState<DocumentDqMapping | null>(null);
  const [mappings, setMappings] = useState<DocumentDqMapping[]>();
  const [updatingNoteMappingId, setUpdatingNoteMappingId] = useState<number | null>(null);
  const { header } = useAppSelector(selectDocument)?.doc ?? impossible()

  const [dialog, setDialog] = useState<DialogState>("none")
  const [isPreview, setIsPreview] = useState(false);
  const [actionsMenuExpanded, setActionsMenuExpanded] = useState(false);
  const isEditable = useAppSelector(isDocEditable)
  const showComments = useAppSelector(state => showDocComments(state, sectionId))

  const mappingCommentOf = (m: any) =>
    m.mappingComments ?? m.mappingComment ?? "";

  const isProtocol = header.documentKind === "PROTOCOL"
  const expandedActionsColWidth = isProtocol
    ? (showComments ? PROTOCOL_ACTIONS_WITH_COMMENTS_COL_WIDTH : PROTOCOL_ACTIONS_COL_WIDTH)
    : ACTIONS_COL_WIDTH;

  useEffect(() => {
    let ignore = false;
    const loadInitialData = async () => {
      setIsLoading(true);
      setIsError(false);
      setError(null as any);

      try {
        const [mappings] = await Promise.all([
          dispatch(loadDocumentDqMappings({ documentId, sectionId })).then(unwrapResult),
        ]);

        if (ignore) return;
        setMappings(mappings);
      } catch (err) {
        if (ignore) return;
        setIsError(true);
        setError(err as any);
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    loadInitialData();
    return () => { ignore = true; };
  }, [dispatch, documentId, sectionId, reloadToken]);

  const handleEdit = (row: DocumentDqMapping) => {
    setSelectedMapping(row);
    setDialog("addEditDocumentDqMapping");
  };

  const handleDelete = (q: DocumentDqMapping) => {
    setDialog("deleteDocumentDqMapping");
    setSelectedMapping(q);
  };

  const handleComments = (row: DocumentDqMapping) => {
    setSelectedMapping(row);
    setDialog("commentDocumentDqMapping");
  };

  const handleQuickNoteChange = async (mapping: DocumentDqMapping, nextNote: DqMappingNote) => {
    if (!isEditable || isProtocol || updatingNoteMappingId !== null) {
      return;
    }

    setUpdatingNoteMappingId(mapping.id);
    try {
      const updated = await dispatch(updateDqMappingForReport({
        documentId,
        sectionId: String(sectionId),
        id: mapping.id,
        payload: {
          note: nextNote,
          mappingComment: mappingCommentOf(mapping),
          documents: mapping.docMappings ?? [],
        },
      })).then(unwrapResult) as DocumentDqMapping;

      setMappings(produce(draft => {
        const index = draft?.findIndex(item => item.id === updated.id) ?? -1;
        if (draft && index >= 0) {
          draft[index] = updated;
        }
      }));
      dispatch(showSuccess("saved"));
    } catch {
      dispatch(showError("error"));
    } finally {
      setUpdatingNoteMappingId(null);
    }
  };


  const handleDialogCancelClick = useCallback(() => {
    setDialog("none")
    setSelectedMapping(null);
  }, [])



  const onSuccessfullyGeneratedMappings = (mappings: DocumentDqMapping[]) => {
    setMappings(() => [...mappings]);
    setDialog("none")
  };

  const onSuccessfullyMappingSave = (mapping: DocumentDqMapping) => {
    setMappings(produce(draft => {
      if (draft !== undefined) {
        const index = draft.findIndex(m => m.id === mapping.id);

        if (index !== -1) {
          draft[index] = mapping;
        } else {
          draft.push(mapping);
        }
      }
    }));
    setDialog("none")
  };

  const onBulkAssessmentSaved = (updatedMappings: DocumentDqMapping[]) => {
    const updatedById = new Map(updatedMappings.map(mapping => [mapping.id, mapping]));
    setMappings(current => current?.map(mapping => updatedById.get(mapping.id) ?? mapping));
    setDialog("none");
  };

  const onMappingDeleted = (mappingId: number) => {
    setMappings(produce(draft => {
      if (draft !== undefined) {
        const index = draft.findIndex(m => m.id === mappingId);
        if (index !== -1) {
          draft.splice(index, 1);
        }
      }
    }));
    setSelectedMapping(null);
    setDialog("none");
  };

  const [expandedDocs, setExpandedDocs] = useState<Record<string, boolean>>({});

  const toggleDoc = (mappingId: number, docKey: number | string) => {
    const key = `${mappingId}:${docKey}`;
    setExpandedDocs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const documentExpansionKeys = (mappings ?? []).flatMap(mapping =>
    (mapping.docMappings ?? []).map((document, index) => `${mapping.id}:${document.id ?? index}`)
  );
  const allDocumentsExpanded = documentExpansionKeys.length > 0
    && documentExpansionKeys.every(key => expandedDocs[key]);

  const handleToggleAllDocuments = () => {
    if (allDocumentsExpanded) {
      setExpandedDocs({});
      return;
    }

    setExpandedDocs(Object.fromEntries(documentExpansionKeys.map(key => [key, true])));
  };

  if (isError) {
    return <>
      <ErrorContainer error={error} />
    </>
  }

  if (isLoading) {
    return <>
      <LoadingContainer />
    </>
  }

  return (
    <>
      {enablePreview && (
        <Paper
          variant="outlined"
          sx={{
            mb: 1,
            px: 1,
            py: 0.75,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "wrap",
            borderRadius: 1,
            bgcolor: "primary.400",
            borderColor: "primary.400",
          }}
        >
          {isProtocol && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Add />}
              disabled={!isEditable}
              onClick={() => setDialog("addEditDocumentDqMapping")}
            >
              <Localized id="document-dq-mapping-toolbar-add"><span>_Add</span></Localized>
            </Button>
          )}
          {isProtocol && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<SettingsSuggestIcon />}
              disabled={!isEditable}
              onClick={() => setDialog("generateDocumentDqMappings")}
            >
              <Localized id="document-dq-mapping-toolbar-generate-urs"><span>_From URS</span></Localized>
            </Button>
          )}
          {!isProtocol && aiGenerationAction}
          {!isProtocol && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<PlaylistAddCheckIcon />}
              disabled={!isEditable || !mappings?.length}
              onClick={() => setDialog("bulkAssessDocumentDqMappings")}
            >
              <Localized id="document-dq-mapping-bulk-assessment"><span>_Bulk assessment</span></Localized>
            </Button>
          )}

          <Box sx={{ flex: 1 }} />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title={<Localized id="document-dq-mapping-toolbar-quick-assessment-tooltip"><span>_Show or hide quick assessment</span></Localized>}>
            <Button
              size="small"
              variant={actionsMenuExpanded ? "outlined" : "text"}
              color={actionsMenuExpanded ? "primary" : "inherit"}
              startIcon={<FactCheckIcon />}
              aria-pressed={actionsMenuExpanded}
              onClick={() => setActionsMenuExpanded(current => !current)}
              sx={actionsMenuExpanded ? {
                bgcolor: "action.selected",
                fontWeight: 600,
              } : undefined}
            >
              <Localized id="document-dq-mapping-toolbar-quick-assessment"><span>_Quick assessment</span></Localized>
            </Button>
          </Tooltip>
          <Tooltip title={<Localized id="document-dq-mapping-toolbar-expand-tooltip"><span>_Expand or collapse all documents</span></Localized>}>
            <Button
              size="small"
              variant={allDocumentsExpanded ? "outlined" : "text"}
              color={allDocumentsExpanded ? "primary" : "inherit"}
              startIcon={<UnfoldMoreIcon />}
              disabled={documentExpansionKeys.length === 0 || isPreview}
              aria-pressed={allDocumentsExpanded}
              onClick={handleToggleAllDocuments}
              sx={allDocumentsExpanded ? {
                bgcolor: "action.selected",
                fontWeight: 600,
              } : undefined}
            >
              <Localized id="document-dq-mapping-toolbar-expand"><span>_Expand</span></Localized>
            </Button>
          </Tooltip>
          <Tooltip title={<Localized id="document-dq-mapping-toolbar-preview-tooltip"><span>_Show or hide print preview</span></Localized>}>
            <Button
              size="small"
              variant={isPreview ? "outlined" : "text"}
              color={isPreview ? "primary" : "inherit"}
              startIcon={<PrintIcon />}
              aria-pressed={isPreview}
              onClick={() => setIsPreview(current => !current)}
              sx={isPreview ? {
                bgcolor: "action.selected",
                fontWeight: 600,
              } : undefined}
            >
              <Localized id="document-dq-mapping-toolbar-preview"><span>_Preview</span></Localized>
            </Button>
          </Tooltip>
        </Paper>
      )}
      {enablePreview && isPreview ? (

        <PrintPreviewDocumentDqMappingsView
          mappings={mappings ?? []}
        />
      ) : (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 430px)", minHeight: 0 }}>
            <TableContainer sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: NUM_COL_WIDTH }} />
                  <col style={{ width: REQ_COL_PCT }} />
                  <col />
                  <col style={{ width: NOTE_COL_WIDTH }} />
                  <col style={{ width: actionsMenuExpanded ? expandedActionsColWidth : COLLAPSED_ACTIONS_COL_WIDTH }} />
                </colgroup>
                <TableHead
                  sx={{
                    "& .MuiTableRow-root": { height: 40 },
                    "& .MuiTableRow-root:nth-of-type(1) .MuiTableCell-stickyHeader": { top: 0, zIndex: 4 },
                    "& .MuiTableRow-root:nth-of-type(2) .MuiTableCell-stickyHeader": { top: 40, zIndex: 3 },
                    "& .MuiTableRow-root:nth-of-type(3) .MuiTableCell-stickyHeader": { top: 80, zIndex: 2 },
                  }}
                >
                  <TableRow>
                    <TableCell colSpan={5} sx={{ bgcolor: "#f9f9f9", fontWeight: "bold" }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <FactCheckIcon fontSize="medium" sx={{ mr: 1, color: "text.secondary" }} />
                        <Localized id="document-dq-mapping-table-title">
                          <span>_Mapowanie URS</span>
                        </Localized>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }} colSpan={2} align="center">URS</TableCell>
                    <TableCell colSpan={2} align="center">
                      <Localized id="document-dq-mapping-table-header"><span>_Supplier docs</span></Localized>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 120 }} />
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ width: NUM_COL_WIDTH, bgcolor: "#f5f5f5" }}>
                      <Localized id="urs-requirement-no"><span>_Nr</span></Localized>
                    </TableCell>
                    <TableCell sx={{ width: REQ_COL_PCT, bgcolor: "#f5f5f5" }}>
                      <Localized id="urs-requirement-req"><span>_Wymaganie</span></Localized>
                    </TableCell>

                    <TableCell sx={{ width: DESC_COL_PCT }}>
                      <Localized id="document"><span>_Dokument</span></Localized>
                    </TableCell>
                    <TableCell sx={{ width: NOTE_COL_WIDTH }}>
                      <Localized id="note"><span>_Ocena</span></Localized>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        width: actionsMenuExpanded ? expandedActionsColWidth : COLLAPSED_ACTIONS_COL_WIDTH,
                        minWidth: actionsMenuExpanded ? expandedActionsColWidth : COLLAPSED_ACTIONS_COL_WIDTH,
                        maxWidth: actionsMenuExpanded ? expandedActionsColWidth : COLLAPSED_ACTIONS_COL_WIDTH,
                        px: actionsMenuExpanded ? 2 : 0.5,
                        borderLeft: 1,
                        borderLeftColor: "divider",
                      }}
                    />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {mappings?.map((m: DocumentDqMapping) => (
                    <TableRow key={`${m.id}`} hover sx={{
                      "& > td:nth-of-type(1), & > td:nth-of-type(2)": {
                        bgcolor: "#f9f9f9"
                      },
                    }}>
                      <TableCell sx={{ width: 60 }}>
                        <DocumentNumberBox value={m.requirementNumber} />
                      </TableCell>

                      {/* URS */}
                      <TableCell sx={{ width: REQ_COL_PCT }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Box sx={{ color: "text.secondary", whiteSpace: "pre-wrap" }}>
                            <div dangerouslySetInnerHTML={{ __html: m.requirementDescription }} />
                          </Box>
                          <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                            <DocumentOriginIcon origin={m.requirementOrigin} />
                            {m.isRequirementFixed && (
                              <LockedIconWithTooltip />)}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Dokumenty*/}
                      <TableCell sx={{ width: DOC_COL_PCT }}>
                        {(() => {
                          const docs = [...(m.docMappings ?? [])].sort((a, b) => a.order - b.order);
                          if (docs.length === 0) {
                            return <></>;
                          }

                          return (
                            <Stack spacing={0.75}>
                              {docs.map((d, i) => {
                                const key = `${m.id}:${d.id ?? i}`;
                                const isOpen = !!expandedDocs[key];

                                return (
                                  <Box key={key}>
                                    {/* Linia skrótu */}
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={1}
                                      sx={{ cursor: "pointer" }}
                                      onClick={() => toggleDoc(m.id, d.id ?? i)}
                                    >
                                      <IconButton size="small" sx={{ p: 0.25 }}>
                                        {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                      </IconButton>

                                      <Chip size="small" label={`#${i + 1}`} />
                                      <Box>
                                        <Typography variant="subtitle2" sx={{ minWidth: 0 }} noWrap>
                                          {d.docTitle || "(bez tytułu)"}
                                        </Typography>
                                        <DocumentNumberBox value={d.docNumber} />
                                      </Box>
                                      <Box sx={{ flex: 1 }} />
                                      <DocumentOriginIcon origin={d.docOrigin} />
                                      {d.isFsFixed && <LockedIconWithTooltip />}

                                    </Stack>

                                    {/* Szczegóły po rozwinięciu (opis + komentarz) */}
                                    {isOpen && (
                                      <Box sx={{ mt: 1, pl: 4 }}>
                                        <ClampHtml html={d.docDescription ?? ""} lines={6} />
                                        {!!d.mappingComment && (
                                          <Box sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary" }}>
                                            <ChatBubbleOutlineIcon fontSize="small" />
                                            <Box
                                              sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                                              title={d.mappingComment}
                                            >
                                              {d.mappingComment}
                                            </Box>
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Stack>
                          );
                        })()}
                      </TableCell>

                      <TableCell sx={{ width: NOTE_COL_WIDTH, py: 0.5 }}>
                        <Stack spacing={0.75} alignItems="flex-start">
                          <NoteChip note={(m as any).note as DqMappingNote} />
                          {!!mappingCommentOf(m) && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 0.5,
                                color: "text.secondary",
                                minWidth: 0,
                              }}
                            >
                              <ChatBubbleOutlineIcon fontSize="small" sx={{ mt: 0.125, flexShrink: 0 }} />
                              <Typography
                                variant="caption"
                                title={mappingCommentOf(m)}
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  whiteSpace: "pre-wrap",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {mappingCommentOf(m)}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </TableCell>

                      {/* Akcje */}
                      <TableCell
                        align="right"
                        sx={{
                          width: actionsMenuExpanded ? expandedActionsColWidth : COLLAPSED_ACTIONS_COL_WIDTH,
                          minWidth: actionsMenuExpanded ? expandedActionsColWidth : COLLAPSED_ACTIONS_COL_WIDTH,
                          maxWidth: actionsMenuExpanded ? expandedActionsColWidth : COLLAPSED_ACTIONS_COL_WIDTH,
                          px: actionsMenuExpanded ? 2 : 0.5,
                          whiteSpace: "nowrap",
                          borderLeft: 1,
                          borderLeftColor: "divider",
                        }}
                      >
                        <If condition={hasAction(actions, "Edit")}>
                          <>
                            {actionsMenuExpanded && !isProtocol && ([
                              { note: "OK" as DqMappingNote, labelId: "document-dq-mapping-note-ok", OutlineIcon: CheckCircleOutlineIcon, FilledIcon: CheckCircleIcon, color: "success.main" },
                              { note: "FAIL" as DqMappingNote, labelId: "document-dq-mapping-note-fail", OutlineIcon: CancelOutlinedIcon, FilledIcon: CancelIcon, color: "error.main" },
                              { note: "NA" as DqMappingNote, labelId: "document-dq-mapping-note-na", OutlineIcon: RemoveCircleOutlineIcon, FilledIcon: RemoveCircleIcon, color: "text.secondary" },
                            ]).map(({ note, labelId, OutlineIcon, FilledIcon, color }) => {
                              const selected = m.note === note;
                              const AssessmentIcon = selected ? FilledIcon : OutlineIcon;
                              return (
                                <Tooltip key={note} title={<Localized id={labelId}><span>_{note}</span></Localized>}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={!isEditable || updatingNoteMappingId !== null}
                                      onClick={() => void handleQuickNoteChange(m, note)}
                                      sx={{
                                        color: selected ? color : "action.active",
                                        bgcolor: selected ? "action.selected" : "transparent",
                                        "&:hover": { color, bgcolor: "action.hover" },
                                      }}
                                    >
                                      <AssessmentIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              );
                            })}
                            <Tooltip title={<Localized id='edit'><span>_edit</span></Localized>}>
                              <IconButton
                                onClick={() => handleEdit(m)}
                                sx={{ color: "action.active", "&:hover": { color: "info.main" } }}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={<Localized id='delete'><span>_delete</span></Localized>}>
                              <IconButton
                                onClick={() => handleDelete(m)}
                                sx={{ color: "action.active", "&:hover": { color: "error.main" } }}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </>
                        </If>
                        <If condition={actionsMenuExpanded && showComments}>
                          {(
                            <AllCommentsCounter sectionId={sectionId} contextGuid={m.guid}>
                              <Tooltip title={<Localized id='comments'><span>_Comments</span></Localized>}>
                                <IconButton
                                  onClick={() => handleComments(m)}
                                  sx={{ color: "action.active", "&:hover": { color: "info.main" } }}
                                >
                                  <CommentIcon />
                                </IconButton>
                              </Tooltip>
                            </AllCommentsCounter>
                          )}
                        </If>

                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>


            {dialog === "generateDocumentDqMappings" && (
              <GenerateDqMappingsDialog
                documentId={documentId}
                sectionId={sectionId}
                onSuccessfulSave={onSuccessfullyGeneratedMappings}
                onCancel={handleDialogCancelClick}
              />
            )}
            {dialog === "bulkAssessDocumentDqMappings" && (
              <BulkAssessDocumentDqMappingsDialog
                documentId={documentId}
                sectionId={sectionId}
                mappings={mappings ?? []}
                onClose={handleDialogCancelClick}
                onSaved={onBulkAssessmentSaved}
              />
            )}
            {dialog === "addEditDocumentDqMapping" && (
              <AddEditDocumentDqMapping
                documentId={documentId}
                isProtocol={isProtocol}
                sectionId={sectionId}
                mappingRow={selectedMapping}
                mode={selectedMapping ? "edit" : "create"}
                onCancelClick={handleDialogCancelClick}
                onSuccessfulSave={onSuccessfullyMappingSave}
              />
            )}
            {dialog === "commentDocumentDqMapping" && (
              <AddEditDocumentDqMapping
                documentId={documentId}
                isProtocol={isProtocol}
                sectionId={sectionId}
                mappingRow={selectedMapping}
                mode={"comment"}
                onCancelClick={handleDialogCancelClick}
                onSuccessfulSave={onSuccessfullyMappingSave}
              />
            )}
            {
              dialog === "deleteDocumentDqMapping" && selectedMapping?.id &&
              <DeleteDqMappingDialog
                documentId={documentId}
                sectionId={sectionId}
                mappingId={selectedMapping.id}
                onSuccessfulSave={onMappingDeleted}
                onCancel={handleDialogCancelClick}
              />
            }
          </Box>
        </>
      )}
    </>
  );
};
